/**
 * Refresh Stage 2: SERP Re-Analysis
 *
 * Same UI pattern as Stage2aSerpV2 but for the refresh pipeline.
 * Shows the primary keyword (set by the audit stage), accepts SERP CSV
 * + Google results as ephemeral inputs, displays the analysis output.
 */

import React, { useCallback, useState } from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { useCsvUpload } from '../shared/useCsvUpload';
import { KeywordSelector } from '../shared/KeywordSelector';
import type { ArticleDocument } from '../types';

const DECISION_COLORS: Record<string, string> = {
  invest: 'bg-green-100 text-green-700',
  hold: 'bg-blue-100 text-blue-700',
  pivot: 'bg-amber-100 text-amber-700',
  abandon: 'bg-red-100 text-red-700',
};

function renderOutput(doc: ArticleDocument) {
  const serp = doc.targeting?.serp as any;
  if (!serp?.overview) {
    return <div className="text-sm text-gray-500">No SERP analysis yet.</div>;
  }
  const o = serp.overview;
  const cpa = serp.current_position_assessment || {};
  const gaps = serp.content_gap_signals || {};

  return (
    <div className="space-y-3">
      {/* Decision */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">SERP Decision</div>
        <span className={`text-sm font-semibold px-3 py-1 rounded-full ${DECISION_COLORS[o.decision] || 'bg-gray-100 text-gray-700'}`}>
          {o.decision}
        </span>
      </div>
      {o.decision_rationale && <p className="text-sm text-gray-600">{o.decision_rationale}</p>}

      {/* Position assessment */}
      {cpa.realistic_ceiling && (
        <div className="bg-gray-50 rounded border p-2 text-xs space-y-1">
          <div className="font-medium text-gray-700">Competitive Position</div>
          <div><span className="text-gray-400">Realistic ceiling:</span> {cpa.realistic_ceiling}</div>
          {cpa.domains_we_can_beat?.length > 0 && (
            <div><span className="text-gray-400">Beatable above us:</span> {cpa.domains_we_can_beat.join(', ')}</div>
          )}
          {cpa.entrenched_above_us?.length > 0 && (
            <div><span className="text-gray-400">Entrenched above us:</span> {cpa.entrenched_above_us.join(', ')}</div>
          )}
          {cpa.displacement_threats && (
            <div><span className="text-gray-400">Displacement threats:</span> {cpa.displacement_threats}</div>
          )}
        </div>
      )}

      {/* Content gaps */}
      {(gaps.dominant_subtopics_above_us?.length > 0 || gaps.our_differentiator_viability) && (
        <div className="bg-amber-50 rounded border border-amber-200 p-2 text-xs space-y-1">
          <div className="font-medium text-amber-700">Content Gaps</div>
          {gaps.dominant_subtopics_above_us?.length > 0 && (
            <div><span className="text-amber-600">Topics above us we lack:</span> {gaps.dominant_subtopics_above_us.join(', ')}</div>
          )}
          {gaps.our_differentiator_viability && (
            <div><span className="text-amber-600">Our angle viability:</span> {gaps.our_differentiator_viability}</div>
          )}
        </div>
      )}

      {o.dominant_content_format && (
        <p className="text-xs text-gray-500">
          <span className="text-gray-400">Dominant format:</span> {o.dominant_content_format}
        </p>
      )}

      {serp.paa_questions?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">People Also Ask</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {serp.paa_questions.map((q: string, i: number) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}

      {(doc.targeting?.competitors?.urls || []).length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">Competitor URLs ({(doc.targeting?.competitors?.urls || []).length})</div>
          {(doc.targeting?.competitors?.urls || []).map((u: any, i: number) => (
            <div key={i} className="text-xs text-gray-600 flex items-center gap-2">
              <span>#{u.serp_position || u.position}</span>
              <span className="font-medium">{u.domain}</span>
              {u.above_or_below_us && (
                <span className={`text-[10px] px-1 rounded ${u.above_or_below_us === 'above' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                  {u.above_or_below_us}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RefreshSerpV2() {
  const { document, run, executeStage } = useArticleContext();
  const kw = document?.targeting?.keywords;
  const primaryKeyword = kw?.primary?.keyword;
  const secondaryKeywords = (kw?.secondary || []).map((k: any) => typeof k === 'string' ? k : k?.keyword).filter(Boolean);
  const hasPrimary = Boolean(primaryKeyword);

  // Build keyword options for the shared selector
  const keywordOptions = [
    ...(primaryKeyword ? [{ keyword: primaryKeyword, volume: kw?.primary?.volume, kd: kw?.primary?.kd }] : []),
    ...secondaryKeywords.filter((k: string) => k !== primaryKeyword).map((k: string) => ({ keyword: k })),
  ];

  const serpFilter = useCallback((rows: Record<string, string>[]) => {
    return rows.filter((row) => {
      const t = String(row.Type || '').trim();
      return t === 'Organic' || t === 'AI Overview';
    }).slice(0, 20);
  }, []);
  const { rawCsv, setRawCsv, fileName, fileRef, handleFileUpload } = useCsvUpload({ filterRows: serpFilter });
  const [googleResults, setGoogleResults] = useState('');
  const [running, setRunning] = useState(false);

  const handleRun = useCallback(async () => {
    setRunning(true);
    try {
      await executeStage('refresh_2_serp', {
        payload: {
          semrush_serp_csv: rawCsv,
          google_results: googleResults,
        },
      });
    } finally {
      setRunning(false);
    }
  }, [executeStage, rawCsv, googleResults]);

  return (
    <StagePanel
      stageId="refresh_2_serp"
      isLlmStage
      hideRunButton
      title="Refresh: SERP Re-Analysis"
      description="Re-analyzes the SERP landscape for the article's target keyword."
      renderOutput={renderOutput}
    >
      <div className="space-y-3">
        {/* Primary keyword selector */}
        <KeywordSelector options={keywordOptions} />

        {/* SEMrush CSV upload */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            SEMrush SERP CSV (top 20 organic + AI Overview results)
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="text-xs"
              onChange={handleFileUpload}
            />
            {fileName && <span className="text-xs text-green-600">{fileName}</span>}
          </div>
          {rawCsv && (
            <textarea
              className="mt-1 w-full text-xs font-mono border rounded p-1 h-20 text-gray-600"
              value={rawCsv}
              onChange={(e) => setRawCsv(e.target.value)}
            />
          )}
        </div>

        {/* Google results */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            Google SERP results (paste the search results page)
          </label>
          <textarea
            className="w-full text-xs border rounded p-2 h-24"
            value={googleResults}
            onChange={(e) => setGoogleResults(e.target.value)}
            placeholder="Paste Google SERP results here..."
          />
        </div>

        <button
          type="button"
          className="px-4 py-2 text-sm font-medium rounded-md text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#1a2332' }}
          disabled={running || !hasPrimary || !rawCsv.trim() || !googleResults.trim()}
          title={!hasPrimary ? 'Requires primary keyword from Audit stage' : undefined}
          onClick={handleRun}
        >
          {running ? 'Analyzing…' : 'Analyze SERP →'}
        </button>
      </div>
    </StagePanel>
  );
}
